import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import { CardGiftcard, LocalActivity, NotificationsActive } from "@material-ui/icons"
import React, { useState } from "react"
import type { CupomEstabelecimentoItem } from "../../../services/cupomEstabelecimento"
import { obterCuponsEstabelecimento } from "../../../services/cupomEstabelecimento"
import { useSnackbar } from "../../../utils/notificacoes"
import { useEdicaoClienteContext } from "../context/useEdicaoClienteContext"
import { isDataValida } from "../models/edicaoClienteModel"
import { useCardStyles } from "./estilosCards"

type DialogoAberto = "selos" | "cupom" | "notificacao" | null

export function InteracoesCliente() {
    const classes = useCardStyles()
    const { dados, bonificarSelos, adicionarAoCupom, agendarNotificacao } =
        useEdicaoClienteContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const [dialogo, setDialogo] = useState<DialogoAberto>(null)

    const [quantidade, setQuantidade] = useState("1")

    const [cupons, setCupons] = useState<CupomEstabelecimentoItem[]>([])
    const [idCupom, setIdCupom] = useState("")

    const [titulo, setTitulo] = useState("")
    const [mensagem, setMensagem] = useState("")
    const [dataEnvio, setDataEnvio] = useState("")
    const [horaEnvio, setHoraEnvio] = useState("")

    const podeBonificar = dados.permissoes.includes("bonificar_cliente")
    const podeNotificar = dados.permissoes.includes("notificacao")

    if (!podeBonificar && !podeNotificar) return null

    const fechar = () => setDialogo(null)

    const abrirCupom = async () => {
        setDialogo("cupom")
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        const response = await obterCuponsEstabelecimento(autorizacao)
        if ("retornoErro" in response) {
            alertStart(response.mensagem || "Erro ao carregar os cupons", "error")
            return
        }
        setCupons(response)
    }

    const handleBonificar = async () => {
        const valor = Number(quantidade)
        if (!Number.isInteger(valor) || valor < 1 || valor > 10) {
            alertStart("A quantidade deve ser um valor inteiro entre (1 e 10)", "error")
            return
        }
        try {
            alertStart(await bonificarSelos(valor), "success")
            fechar()
        } catch (error) {
            alertStart((error as Error).message || "Erro ao bonificar o cliente", "error")
        }
    }

    const handleAdicionarCupom = async () => {
        if (!idCupom) {
            alertStart("Selecione um cupom.", "error")
            return
        }
        try {
            alertStart(await adicionarAoCupom(Number(idCupom)), "success")
            setIdCupom("")
            fechar()
        } catch (error) {
            alertStart((error as Error).message || "Erro ao adicionar o cliente ao cupom", "error")
        }
    }

    const handleAgendarNotificacao = async () => {
        if (!titulo.trim()) {
            alertStart("Informe o título da notificação.", "error")
            return
        }
        if (mensagem.trim().length < 3) {
            alertStart("A mensagem deve ter ao menos 3 caracteres.", "error")
            return
        }
        if (!isDataValida(dataEnvio)) {
            alertStart("Data de envio inválida.", "error")
            return
        }
        if (!/^\d{2}:\d{2}$/.test(horaEnvio)) {
            alertStart("Hora de envio inválida.", "error")
            return
        }
        try {
            const retorno = await agendarNotificacao({
                titulo: titulo.trim(),
                mensagem: mensagem.trim(),
                data: dataEnvio,
                hora: horaEnvio,
            })
            alertStart(retorno, "success")
            setTitulo("")
            setMensagem("")
            setDataEnvio("")
            setHoraEnvio("")
            fechar()
        } catch (error) {
            alertStart((error as Error).message || "Erro ao agendar a notificação", "error")
        }
    }

    return (
        <Paper className={classes.configCard}>
            <Box className={classes.cardHeader}>
                <CardGiftcard className={classes.iconHeader} />
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    Interações com o cliente
                </Typography>
            </Box>

            <Box className={classes.cardContent} display="flex" flexWrap="wrap" gridGap={12}>
                {podeBonificar && (
                    <>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CardGiftcard />}
                            onClick={() => setDialogo("selos")}
                        >
                            Bonificar com Selos
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<LocalActivity />}
                            onClick={abrirCupom}
                        >
                            Adicionar Cliente ao Cupom
                        </Button>
                    </>
                )}
                {podeNotificar && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<NotificationsActive />}
                        onClick={() => setDialogo("notificacao")}
                    >
                        Enviar Notificação Agendada
                    </Button>
                )}
            </Box>

            <Dialog open={dialogo === "selos"} onClose={fechar} fullWidth maxWidth="xs">
                <DialogTitle>Bonificar com Selos</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Quantidade"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        inputProps={{ min: 1, max: 10 }}
                        value={quantidade}
                        onChange={e => setQuantidade(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={fechar}>Cancelar</Button>
                    <Button
                        variant="contained"
                        className={classes.botaoSalvar}
                        onClick={handleBonificar}
                    >
                        Bonificar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogo === "cupom"} onClose={fechar} fullWidth maxWidth="xs">
                <DialogTitle>Adicionar Cliente ao Cupom</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        label="Cupom"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={idCupom}
                        onChange={e => setIdCupom(e.target.value)}
                    >
                        <MenuItem value="">Selecione um cupom</MenuItem>
                        {cupons.map(cupom => (
                            <MenuItem key={cupom.cupomId} value={String(cupom.cupomId)}>
                                {cupom.nomeCupom}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={fechar}>Cancelar</Button>
                    <Button
                        variant="contained"
                        className={classes.botaoSalvar}
                        onClick={handleAdicionarCupom}
                    >
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogo === "notificacao"} onClose={fechar} fullWidth maxWidth="sm">
                <DialogTitle>Enviar Notificação Agendada</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" className={classes.legenda} gutterBottom>
                        A notificação será enviada para o cliente; o prazo para que ele receba a
                        mensagem é de até 30 minutos após o horário agendado.
                    </Typography>
                    <TextField
                        label="Título"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        inputProps={{ maxLength: 120 }}
                        value={titulo}
                        onChange={e => setTitulo(e.target.value)}
                    />
                    <TextField
                        label="Data de envio"
                        type="date"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        value={dataEnvio}
                        onChange={e => setDataEnvio(e.target.value)}
                    />
                    <TextField
                        label="Hora de envio"
                        type="time"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        value={horaEnvio}
                        onChange={e => setHoraEnvio(e.target.value)}
                    />
                    <TextField
                        label="Mensagem"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="dense"
                        multiline
                        minRows={3}
                        inputProps={{ maxLength: 2000 }}
                        value={mensagem}
                        onChange={e => setMensagem(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={fechar}>Cancelar</Button>
                    <Button
                        variant="contained"
                        className={classes.botaoSalvar}
                        onClick={handleAgendarNotificacao}
                    >
                        Enviar
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
